// One editable value in a list row — a name, a cell, a note. Shown as text; a click turns
// it into a box, Enter or leaving the box saves, Escape leaves it as it was. A date cell
// is typed through DateControl and shown through DateText, like every date in the app.
// `suggestions` (the grouping column's values already used) are offered while typing.
import { useId, useRef, useState } from 'react';
import DateText from '../DateText';
import DateControl from '../DateField';

const inputType = { phone: 'tel', email: 'email', number: 'text' };

export default function ListCell({ value, onSave, kind = 'text', label, readOnly, className = '', suggestions = null }) {
  const listId = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const cancelling = useRef(false);

  const shown = kind === 'date' && value ? <DateText value={value} /> : value;
  if (readOnly) return <span className={className}>{shown}</span>;

  if (!editing) {
    return (
      <button
        type="button"
        className={`list-cell ${className}`}
        onClick={() => { setDraft(value || ''); setEditing(true); }}
        aria-label={label}
      >
        {shown || ' '}
      </button>
    );
  }

  const finish = (next) => {
    setEditing(false);
    if (cancelling.current) { cancelling.current = false; return; }
    if ((next ?? '').trim() !== (value || '')) onSave((next ?? '').trim());
  };

  if (kind === 'date') {
    return (
      <span className="list-cell-date" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setEditing(false); }}>
        <DateControl value={value || ''} label={label} onChange={(e) => { const v = e.target ? e.target.value : e; finish(v); }} autoFocus />
      </span>
    );
  }

  return (
    <>
    <input
      className="list-cell-input"
      list={suggestions && suggestions.length ? listId : undefined}
      type={inputType[kind] || 'text'}
      inputMode={kind === 'number' ? 'decimal' : undefined}
      dir={kind === 'phone' || kind === 'email' ? 'ltr' : undefined}
      autoFocus
      value={draft}
      aria-label={label}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => finish(draft)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') { cancelling.current = true; e.currentTarget.blur(); }
      }}
    />
    {suggestions && suggestions.length > 0 && (
      <datalist id={listId}>{suggestions.map((v) => <option key={v} value={v} />)}</datalist>
    )}
    </>
  );
}
